import React, { useEffect, useState } from 'react';
import { G, styles, api, fmtUsd } from '../theme.js';
import { Btn, Badge, Spinner, Modal, Field, Input, Select } from '../ui.jsx';
import { WatchPanel } from '../widgets/WatchPanel.jsx';

export function Financials({ toast, user, go }) {
  const [data, setData] = useState(null);
  const [range, setRange] = useState('ytd');
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null); // 'revenue' | 'expense' | null
  // Super-admin can flip between sub-accounts inline — no need to ghost in/out.
  const isSuperAdmin = user?.role === 'super_admin';
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState(() => user?.accountId || '');

  useEffect(() => {
    if (isSuperAdmin) {
      api('/api/agency/accounts').then(list => {
        setAccounts(list);
        // Default to the ghosted account if any, else the first account.
        if (!accountId && list[0]) setAccountId(user?.accountId || list[0].id);
      }).catch(()=>{});
    }
  }, [isSuperAdmin]);

  const load = () => {
    if (isSuperAdmin && !accountId) { setData(null); setLoading(false); return; }
    setLoading(true);
    const q = isSuperAdmin && accountId ? `&accountId=${encodeURIComponent(accountId)}` : '';
    api(`/api/financials/dashboard?range=${range}${q}`).then(setData).catch(e=>toast(e.message,'error')).finally(()=>setLoading(false));
  };
  useEffect(() => { load(); }, [range, accountId]);
  // Auto-refresh when the user comes back to the tab (fixes stale-data complaint)
  useEffect(() => {
    const refresh = () => load();
    window.addEventListener('tradeops:tab-focus', refresh);
    return () => window.removeEventListener('tradeops:tab-focus', refresh);
  }, [range, accountId]);

  if (loading || !data) return <div style={{ padding:24 }}>
    {isSuperAdmin && <div style={{ ...styles.card, padding:14, marginBottom:18, borderColor:G.purple+'66', background:'rgba(168,85,247,0.06)' }}>
      <div style={{ fontWeight:700, marginBottom:6 }}>🎯 Viewing financials for:</div>
      <Select value={accountId} onChange={e=>setAccountId(e.target.value)} style={{ width:'100%', maxWidth:420 }}>
        <option value="">— Select account —</option>
        {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
      </Select>
    </div>}
    <div style={{ display:'flex', justifyContent:'center', padding:80 }}><Spinner sz={32}/></div>
  </div>;

  const profit = data.netProfit.total;
  const profitColor = profit >= 0 ? G.green : G.red;

  return <div style={{ padding:24 }}>
    {isSuperAdmin && accounts.length > 0 && <div style={{ ...styles.card, padding:'10px 14px', marginBottom:14, borderColor:G.purple+'66', background:'rgba(168,85,247,0.06)', display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
      <span style={{ fontWeight:700, fontSize:13 }}>🎯 Account:</span>
      <Select value={accountId} onChange={e=>setAccountId(e.target.value)} style={{ minWidth:280, flex:'0 1 420px' }}>
        {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
      </Select>
      <span style={{ fontSize:11, color:G.textMuted }}>Switch instantly — no need to ghost.</span>
    </div>}
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, gap:14, flexWrap:'wrap' }}>
      <div>
        <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>Financial Dashboard</h1>
        <div style={{ fontSize:13, color:G.textMuted, marginTop:4 }}>Real-time P&L, payroll, team utilization, and AR aging.</div>
      </div>
      <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
        <div style={{ display:'flex', gap:6, background:G.input, padding:4, borderRadius:10 }}>
          {[['mtd','Month'],['ytd','YTD'],['last30','30d'],['last90','90d']].map(([v,l]) => (
            <Btn key={v} sz="sm" v={range===v?'primary':'ghost'} onClick={()=>setRange(v)}>{l}</Btn>
          ))}
        </div>
        <Btn sz="sm" v="green" onClick={()=>setAdding('revenue')}>+ Revenue</Btn>
        <Btn sz="sm" v="yellow" onClick={()=>setAdding('expense')}>+ Expense</Btn>
        <DownloadMenu/>
      </div>
    </div>

    <ManualEntryModal kind={adding} onClose={()=>setAdding(null)} onSaved={()=>{ setAdding(null); load(); toast('Saved'); }} toast={toast}/>

    {/* Watch + reconcile — pinned above the KPIs so AR aging, stuck jobs,
        and unprocessed leads surface before P&L vanity numbers. */}
    {go && <WatchPanel go={go} toast={toast} compact/>}

    {/* P&L Cards */}
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:14, marginBottom:20 }}>
      <KpiCard label="Revenue" value={fmtUsd(data.revenue.total)} sub={`${data.revenue.invoiceCount} invoices · avg ${fmtUsd(data.revenue.avgTicket)}`} color={G.cyan}/>
      <KpiCard label="COGS" value={fmtUsd(data.cogs.total)} sub={`${data.cogs.pct}% of revenue`} color={G.orange}/>
      <KpiCard label="Gross Profit" value={fmtUsd(data.grossProfit.total)} sub={`${data.grossProfit.marginPct}% margin`} color={G.green}/>
      <KpiCard label="Payroll" value={fmtUsd(data.payroll.total)} sub={`${data.payroll.hours} hrs worked`} color={G.purple}/>
      <KpiCard label="Operating Expenses" value={fmtUsd(data.expenses.opex ?? data.expenses.total)} sub={`${Object.keys(data.expenses.byCategory).length} categories`} color={G.yellow}/>
      <KpiCard label={profit>=0?'Net Profit':'Net Loss'} value={fmtUsd(Math.abs(profit))} sub={`${data.netProfit.marginPct}% margin`} color={profitColor}/>
    </div>

    {/* Trend chart + P&L Statement */}
    <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:14, marginBottom:20 }}>
      <div style={styles.card}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:18 }}>
          <h3 style={{ margin:0, fontSize:16 }}>Revenue Trend (last 6 months)</h3>
          <Badge c={G.green} bg={G.greenLight}>Avg margin {data.grossProfit.marginPct}%</Badge>
        </div>
        <TrendChart data={data.trend}/>
      </div>
      <div style={styles.card}>
        <h3 style={{ margin:'0 0 14px 0', fontSize:16 }}>P&L Statement</h3>
        <PLRow label="Revenue" value={data.revenue.total} bold/>
        <PLRow label="− COGS" value={-data.cogs.total} indent/>
        <PLRow label="= Gross Profit" value={data.grossProfit.total} bold color={G.green}/>
        <PLRow label="− Payroll" value={-data.payroll.total} indent/>
        <PLRow label="− Operating Expenses" value={-(data.expenses.opex ?? data.expenses.total)} indent/>
        <div style={{ borderTop:`1px solid ${G.border}`, paddingTop:8, marginTop:8 }}>
          <PLRow label="Net Profit" value={data.netProfit.total} bold color={profitColor}/>
        </div>
        <div style={{ marginTop:14, padding:10, background:G.input, borderRadius:8, fontSize:12, color:G.textMuted }}>
          <div>Gross margin: <strong style={{ color:G.text }}>{data.grossProfit.marginPct}%</strong></div>
          <div>Net margin: <strong style={{ color:G.text }}>{data.netProfit.marginPct}%</strong></div>
          <div>Estimate close rate: <strong style={{ color:G.text }}>{data.estimateCloseRate}%</strong></div>
        </div>
      </div>
    </div>

    {/* Financing reconcile — appears when there's at least one financing request */}
    <FinancingReconcilePanel accountId={accountId}/>

    {/* AR Aging + Time Slippage */}
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:20 }}>
      <div style={styles.card}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
          <h3 style={{ margin:0, fontSize:16 }}>Accounts Receivable Aging</h3>
          <Badge c={G.orange} bg={G.orangeLight}>{fmtUsd(data.ar.total)} open</Badge>
        </div>
        {Object.entries(data.ar.buckets).map(([bucket, val]) => {
          const pct = data.ar.total > 0 ? (val/data.ar.total)*100 : 0;
          const color = bucket==='current'?G.green:bucket==='1-30'?G.cyan:bucket==='31-60'?G.yellow:bucket==='61-90'?G.orange:G.red;
          return <div key={bucket} style={{ marginBottom:10 }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4, fontSize:13 }}>
              <span style={{ color:G.textMuted }}>{bucket==='current'?'Current':bucket+' days'}</span>
              <strong>{fmtUsd(val)}</strong>
            </div>
            <div style={{ height:8, background:G.input, borderRadius:4, overflow:'hidden' }}>
              <div style={{ width:`${pct}%`, height:'100%', background:color }}/>
            </div>
          </div>;
        })}
        <div style={{ marginTop:10, fontSize:12, color:G.textMuted }}>{data.ar.openCount} open invoices</div>
      </div>

      <div style={styles.card}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
          <h3 style={{ margin:0, fontSize:16 }}>Time Slippage</h3>
          <Badge c={data.timeSlippage.slippagePct>10?G.red:data.timeSlippage.slippagePct<-10?G.green:G.yellow}
                 bg={data.timeSlippage.slippagePct>10?G.redLight:data.timeSlippage.slippagePct<-10?G.greenLight:G.yellowLight}>
            {data.timeSlippage.slippagePct>0?'+':''}{data.timeSlippage.slippagePct}%
          </Badge>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
          <div>
            <div style={{ fontSize:11, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:4 }}>Estimated</div>
            <div style={{ fontSize:24, fontWeight:800 }}>{data.timeSlippage.estHours} <span style={{ fontSize:12, color:G.textMuted, fontWeight:500 }}>hrs</span></div>
          </div>
          <div>
            <div style={{ fontSize:11, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:4 }}>Actual</div>
            <div style={{ fontSize:24, fontWeight:800, color: data.timeSlippage.slippagePct>10?G.red:G.text }}>{data.timeSlippage.actHours} <span style={{ fontSize:12, color:G.textMuted, fontWeight:500 }}>hrs</span></div>
          </div>
        </div>
        <div style={{ marginTop:14, padding:12, background:G.input, borderRadius:8, fontSize:13, color:G.textMuted, lineHeight:1.5 }}>
          {data.timeSlippage.completedJobs} jobs completed in this period.
          {data.timeSlippage.slippagePct > 10 && <strong style={{ color:G.red, display:'block', marginTop:6 }}>⚠️ Jobs are running {data.timeSlippage.slippagePct}% over estimate. Review estimates or training.</strong>}
          {data.timeSlippage.slippagePct < -10 && <strong style={{ color:G.green, display:'block', marginTop:6 }}>✓ Beating estimates — consider sharper pricing or growing scope.</strong>}
        </div>
      </div>
    </div>

    {/* Team Utilization */}
    <div style={{ ...styles.card, marginBottom:20 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
        <h3 style={{ margin:0, fontSize:16 }}>Team Utilization & Payroll</h3>
        <Badge c={G.purple} bg={G.purpleDim}>{fmtUsd(data.payroll.total)} total payroll</Badge>
      </div>
      <table style={{ width:'100%', borderCollapse:'collapse' }}>
        <thead><tr>
          {['Tech','Role','Total Hours','Billable','Utilization','Hourly Rate','Wage'].map(h => <th key={h} style={styles.th}>{h}</th>)}
        </tr></thead>
        <tbody>{data.teamUtilization.map(t => (
          <tr key={t.id}>
            <td style={{ ...styles.td, fontWeight:600 }}>{t.name}</td>
            <td style={styles.td}>{t.role}</td>
            <td style={styles.td}>{t.totalHours}</td>
            <td style={styles.td}>{t.billableHours}</td>
            <td style={styles.td}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <div style={{ width:80, height:6, background:G.input, borderRadius:3, overflow:'hidden' }}>
                  <div style={{ width:`${Math.min(100,t.utilizationPct)}%`, height:'100%', background: t.utilizationPct>70?G.green:t.utilizationPct>40?G.yellow:G.red }}/>
                </div>
                <span style={{ fontSize:13, color: t.utilizationPct>70?G.green:t.utilizationPct>40?G.yellow:G.red, fontWeight:600 }}>{t.utilizationPct}%</span>
              </div>
            </td>
            <td style={styles.td}>{fmtUsd(t.hourlyRate)}/hr</td>
            <td style={{ ...styles.td, fontWeight:600 }}>{fmtUsd(t.wage)}</td>
          </tr>
        ))}</tbody>
      </table>
      {data.teamUtilization.length === 0 && <div style={{ padding:32, textAlign:'center', color:G.textMuted }}>No tech timesheets in this period.</div>}
    </div>

    {/* Anomaly Detection */}
    {data.anomalies && data.anomalies.length > 0 && <div style={{ ...styles.card, marginBottom:20, borderColor: G.yellow+'66' }}>
      <h3 style={{ margin:'0 0 14px 0', fontSize:16 }}>⚠️ Things to Watch</h3>
      {data.anomalies.map((a, i) => (
        <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:10, padding:'10px 0', borderBottom: i < data.anomalies.length-1 ? `1px solid ${G.border}` : 'none' }}>
          <Badge c={a.severity==='high'?G.red:G.yellow} bg={(a.severity==='high'?G.red:G.yellow)+'22'}>{a.severity}</Badge>
          <div style={{ flex:1 }}>
            <strong style={{ fontSize:14 }}>{a.title}</strong>
            <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>{a.detail}</div>
            <div style={{ fontSize:12, color:G.purple, marginTop:4, fontWeight:600 }}>→ {a.action}</div>
          </div>
        </div>
      ))}
    </div>}

    {/* Cash Flow Forecast */}
    {data.cashFlowForecast && <div style={{ ...styles.card, marginBottom:20 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
        <h3 style={{ margin:0, fontSize:16 }}>💰 30-Day Cash Flow Forecast</h3>
        <Badge c={data.cashFlowForecast.forecastNet >= 0 ? G.green : G.red} bg={(data.cashFlowForecast.forecastNet >= 0 ? G.green : G.red)+'22'}>
          {data.cashFlowForecast.forecastNet >= 0 ? '+' : ''}{fmtUsd(data.cashFlowForecast.forecastNet)} net
        </Badge>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:12 }}>
        <CashRow label="Revenue run-rate" value={data.cashFlowForecast.expectedFromRunRate || 0} positive sub="based on recent collections"/>
        <CashRow label="Expected from AR" value={data.cashFlowForecast.expectedInflowFromAR} positive sub="weighted by aging"/>
        <CashRow label="From pending estimates" value={data.cashFlowForecast.expectedFromPendingEstimates} positive sub="weighted by close rate"/>
        <CashRow label="Payroll" value={-data.cashFlowForecast.expectedPayroll} sub="based on current pace"/>
        <CashRow label="Operating expenses" value={-data.cashFlowForecast.expectedExpenses} sub="incl. overhead"/>
      </div>
      {data.cashFlowForecast.insufficientData && <div style={{ marginTop:14, padding:12, background:G.input, borderRadius:8, fontSize:13, color:G.textMuted }}>
        ℹ️ Not enough data to forecast runway accurately yet. Need 7+ days of activity.
      </div>}
      {!data.cashFlowForecast.insufficientData && data.cashFlowForecast.runwayDays !== null && <div style={{ marginTop:14, padding:12, background:G.redLight, borderRadius:8, fontSize:13, color:G.red }}>
        ⚠️ Cash runway: ~{data.cashFlowForecast.runwayDays} days at current pace. Accelerate AR collection or cut spend.
      </div>}
    </div>}

    {/* Tech Leaderboard */}
    {data.leaderboard && data.leaderboard.length > 0 && <div style={{ ...styles.card, marginBottom:20 }}>
      <h3 style={{ margin:'0 0 14px 0', fontSize:16 }}>🏆 Tech Leaderboard</h3>
      <table style={{ width:'100%', borderCollapse:'collapse' }}>
        <thead><tr>{['#','Tech','Jobs','Revenue','Avg/Job','On-Time'].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
        <tbody>{data.leaderboard.map((t, i) => (
          <tr key={t.techId}>
            <td style={{ ...styles.td, fontWeight:700, color: i===0?G.yellow:i===1?G.textMuted:i===2?G.orange:G.text }}>{i===0?'🥇':i===1?'🥈':i===2?'🥉':'#'+(i+1)}</td>
            <td style={{ ...styles.td, fontWeight:600 }}>{t.name}</td>
            <td style={styles.td}>{t.jobsCompleted}</td>
            <td style={{ ...styles.td, fontWeight:600 }}>{fmtUsd(t.revenue)}</td>
            <td style={styles.td}>{fmtUsd(t.avgRevenuePerJob)}</td>
            <td style={styles.td}>
              <Badge c={t.onTimePct>=90?G.green:t.onTimePct>=70?G.yellow:G.red} bg={(t.onTimePct>=90?G.green:t.onTimePct>=70?G.yellow:G.red)+'22'}>
                {t.onTimePct}%
              </Badge>
            </td>
          </tr>
        ))}</tbody>
      </table>
    </div>}

    {/* Expenses by category */}
    <div style={styles.card}>
      <h3 style={{ margin:'0 0 14px 0', fontSize:16 }}>Operating Expenses by Category</h3>
      {Object.entries(data.expenses.byCategory).sort((a,b)=>b[1]-a[1]).map(([cat,val]) => {
        const pct = data.expenses.total > 0 ? val/data.expenses.total*100 : 0;
        return <div key={cat} style={{ marginBottom:10 }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4, fontSize:13 }}>
            <span>{cat}</span>
            <strong>{fmtUsd(val)} <span style={{ color:G.textMuted, fontWeight:500 }}>({pct.toFixed(0)}%)</span></strong>
          </div>
          <div style={{ height:6, background:G.input, borderRadius:3, overflow:'hidden' }}>
            <div style={{ width:`${pct}%`, height:'100%', background:G.purple }}/>
          </div>
        </div>;
      })}
      {Object.keys(data.expenses.byCategory).length === 0 && <div style={{ padding:24, textAlign:'center', color:G.textMuted }}>No expenses in this period.</div>}
    </div>

    <QuickBooksPanel toast={toast}/>
  </div>;
}

function DownloadMenu() {
  const [open, setOpen] = useState(false);
  const dl = (kind) => { window.location.href = `/api/financials/export.csv?kind=${kind}`; setOpen(false); };
  return <div style={{ position:'relative' }}>
    <Btn sz="sm" onClick={()=>setOpen(o=>!o)}>⬇ Download</Btn>
    {open && <>
      <div onClick={()=>setOpen(false)} style={{ position:'fixed', inset:0, zIndex:50 }}/>
      <div style={{ position:'absolute', right:0, top:36, background:G.card, border:`1px solid ${G.cardBorder}`, borderRadius:10, padding:6, zIndex:51, minWidth:180, boxShadow:'0 12px 32px rgba(0,0,0,.4)' }}>
        <button onClick={()=>dl('transactions')} style={menuItem}>📊 All Transactions</button>
        <button onClick={()=>dl('invoices')} style={menuItem}>💰 Invoices</button>
        <button onClick={()=>dl('expenses')} style={menuItem}>💸 Expenses</button>
      </div>
    </>}
  </div>;
}
const menuItem = { display:'block', width:'100%', textAlign:'left', background:'none', border:'none', padding:'10px 12px', cursor:'pointer', color:G.text, fontSize:13, borderRadius:6 };

function QuickBooksPanel({ toast }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api('/api/integrations/quickbooks/financials').then(setData).catch(()=>setData({ empty: true }));
  useEffect(() => { load(); }, []);
  const pull = async () => {
    setBusy(true);
    try { await api('/api/integrations/quickbooks/pull', { method:'POST' }); await load(); toast('✓ Pulled fresh data from QuickBooks'); }
    catch (e) { toast(e.message, 'error'); } finally { setBusy(false); }
  };
  if (!data) return null;
  const empty = data.empty || !data.summary;
  const s = data.summary || {};
  return <div style={{ ...styles.card, marginTop:18 }}>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
      <div>
        <h3 style={{ margin:0, fontSize:16 }}>📗 QuickBooks Financials</h3>
        <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>
          {empty ? 'Connect QuickBooks in Integrations to see live P&L here.' :
            `From your QB account · last pulled ${data.cachedAt ? new Date(data.cachedAt).toLocaleString() : '—'}`}
        </div>
      </div>
      {!empty && <Btn sz="sm" v="primary" onClick={pull} disabled={busy}>{busy?<Spinner sz={14}/>:'🔄 Refresh'}</Btn>}
    </div>
    {empty ? (
      <div style={{ padding:14, color:G.textMuted, fontSize:13, textAlign:'center' }}>
        Once connected, we pull P&L summary, A/R aging, and the last 90 days of transactions here for one-stop visibility.
      </div>
    ) : (
      <>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:10 }}>
          <KpiCard label="QB Income" value={fmtUsd(s.income)} color={G.green}/>
          <KpiCard label="COGS" value={fmtUsd(s.cogs)} color={G.orange}/>
          <KpiCard label="Gross Profit" value={fmtUsd(s.grossProfit)} color={G.cyan}/>
          <KpiCard label="Expenses" value={fmtUsd(s.expenses)} color={G.yellow}/>
          <KpiCard label="Net Income" value={fmtUsd(s.netIncome)} color={s.netIncome>=0?G.green:G.red}/>
        </div>
        {data.transactionCount !== undefined && (
          <div style={{ marginTop:12, fontSize:12, color:G.textMuted }}>
            📒 {data.transactionCount} transactions in the last 90 days · Period: {data.period?.start} to {data.period?.end}
          </div>
        )}
      </>
    )}
  </div>;
}

function KpiCard({ label, value, sub, color }) {
  return <div style={{ ...styles.card, borderColor: color+'66' }}>
    <div style={{ fontSize:11, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em' }}>{label}</div>
    <div style={{ fontSize:24, fontWeight:800, color, marginTop:6 }}>{value}</div>
    <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>{sub}</div>
  </div>;
}

function PLRow({ label, value, indent, bold, color }) {
  return <div style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', paddingLeft: indent ? 14 : 0, fontSize:14 }}>
    <span style={{ fontWeight: bold ? 700 : 400, color: color || G.text }}>{label}</span>
    <span style={{ fontWeight: bold ? 700 : 600, color: color || (value < 0 ? G.textMuted : G.text) }}>{fmtUsd(Math.abs(value))}{value<0?'':''}</span>
  </div>;
}

function CashRow({ label, value, positive, sub }) {
  const v = Number(value)||0;
  return <div style={{ ...styles.card, padding:10 }}>
    <div style={{ fontSize:11, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em' }}>{label}</div>
    <div style={{ fontSize:18, fontWeight:800, color: v>=0 ? G.green : G.red, marginTop:4 }}>
      {v>=0?'+':''}{fmtUsd(v)}
    </div>
    {sub && <div style={{ fontSize:11, color:G.textMuted, marginTop:2 }}>{sub}</div>}
  </div>;
}

function TrendChart({ data }) {
  if (!data || !data.length) return <div style={{ height:160, display:'flex', alignItems:'center', justifyContent:'center', color:G.textMuted }}>No data</div>;
  const max = Math.max(...data.map(d => d.revenue), 1);
  return <div style={{ display:'flex', alignItems:'flex-end', gap:14, height:200, paddingTop:24 }}>
    {data.map((d, i) => (
      <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:6 }}>
        <div style={{ fontSize:11, color:G.textMuted, fontWeight:600 }}>{fmtUsd(d.revenue)}</div>
        <div style={{ width:'100%', position:'relative', height:'100%', display:'flex', alignItems:'flex-end' }}>
          <div style={{ width:'100%', height:`${(d.revenue/max)*100}%`, background:`linear-gradient(180deg,${G.cyan},${G.purple})`, borderRadius:'6px 6px 0 0', position:'relative' }}>
            <div style={{ position:'absolute', bottom:0, left:0, width:'100%', height:`${d.cogs > 0 ? (d.cogs/d.revenue)*100 : 0}%`, background:G.orange+'88', borderRadius:'0 0 6px 6px' }}/>
          </div>
        </div>
        <div style={{ fontSize:11, color:G.textMuted, fontWeight:600 }}>{d.month}</div>
      </div>
    ))}
  </div>;
}

function ManualEntryModal({ kind, onClose, onSaved, toast }) {
  const [form, setForm] = useState({});
  useEffect(() => { setForm({}); }, [kind]);
  if (!kind) return null;
  const isRevenue = kind === 'revenue';

  const submit = async () => {
    if (!form.amount || form.amount <= 0) return alert('Amount required');
    try {
      if (isRevenue) {
        await api('/api/admin-panel/manual-revenue', { method:'POST', body: form });
      } else {
        await api('/api/expenses', { method:'POST', body: form });
      }
      onSaved();
    } catch(e) { alert(e.message); }
  };

  return <Modal open onClose={onClose} width={540}>
    <h2 style={{ margin:'0 0 6px 0' }}>{isRevenue ? '💰 Add Revenue (manual)' : '🧾 Add Expense'}</h2>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:14 }}>
      {isRevenue ? 'Use this for cash, check, Venmo, or any payment outside our system. Creates a paid invoice so it shows in P&L.' : 'Quick expense entry — feeds into the financial dashboard immediately.'}
    </div>
    {isRevenue ? <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
      <Field label="Customer name" style={{gridColumn:'1/-1'}}><Input value={form.contactName||''} onChange={e=>setForm({...form, contactName:e.target.value})} placeholder="Cash customer or full name"/></Field>
      <Field label="Service / Description"><Input value={form.service||''} onChange={e=>setForm({...form, service:e.target.value})} placeholder="AC Tune-up, etc."/></Field>
      <Field label="Amount *"><Input type="number" step="0.01" value={form.amount||''} onChange={e=>setForm({...form, amount: Number(e.target.value)})}/></Field>
      <Field label="Paid Date"><Input type="date" value={form.paidDate?form.paidDate.slice(0,10):''} onChange={e=>setForm({...form, paidDate: e.target.value})}/></Field>
      <Field label="Notes" style={{gridColumn:'1/-1'}}><Input value={form.notes||''} onChange={e=>setForm({...form, notes:e.target.value})}/></Field>
    </div>
    : <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
      <Field label="Category *">
        <Select value={form.category||'Parts'} onChange={e=>setForm({...form, category:e.target.value})}>
          {['Parts','Fuel','Insurance','Software','Marketing','Tools','Rent','Phone','Vehicle Maint','Other'].map(c => <option key={c}>{c}</option>)}
        </Select>
      </Field>
      <Field label="Vendor"><Input value={form.vendor||''} onChange={e=>setForm({...form, vendor:e.target.value})}/></Field>
      <Field label="Amount *"><Input type="number" step="0.01" value={form.amount||''} onChange={e=>setForm({...form, amount: Number(e.target.value)})}/></Field>
      <Field label="Date"><Input type="date" value={form.expenseDate||''} onChange={e=>setForm({...form, expenseDate: e.target.value})}/></Field>
      <Field label="Description" style={{gridColumn:'1/-1'}}><Input value={form.description||''} onChange={e=>setForm({...form, description:e.target.value})}/></Field>
    </div>}

    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" onClick={submit}>Add {isRevenue ? 'Revenue' : 'Expense'}</Btn>
    </div>
  </Modal>;
}

// Financing reconcile — shows pending + funded financing requests + referral
// fees earned. Hides itself when there are no requests so the dashboard
// stays clean for accounts not using financing.
function FinancingReconcilePanel({ accountId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    const q = accountId ? `?accountId=${encodeURIComponent(accountId)}` : '';
    api(`/api/financials/financing-reconcile${q}`).then(setData).catch(()=>{});
  }, [accountId]);
  if (!data || !data.requests?.length) return null;
  const STATUS_COLOR = {
    pending: G.textMuted, awaiting_customer: G.cyan, approved: G.blue,
    funded: G.green, declined: G.red, cancelled: G.textDim,
  };
  return <div style={{ ...styles.card, padding:18, marginBottom:20 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14, flexWrap:'wrap', gap:10 }}>
      <h3 style={{ margin:0, fontSize:16 }}>💳 Financing Reconcile</h3>
      <div style={{ display:'flex', gap:10, fontSize:13 }}>
        <span style={{ color:G.textMuted }}>Funded: <strong style={{ color:G.green }}>{fmtUsd(data.summary.totalFunded)}</strong></span>
        <span style={{ color:G.textMuted }}>Pending: <strong style={{ color:G.cyan }}>{fmtUsd(data.summary.totalPending)}</strong></span>
        <span style={{ color:G.textMuted }}>Referral earned: <strong style={{ color:G.purple }}>{fmtUsd(data.summary.totalReferral)}</strong></span>
      </div>
    </div>
    <div style={{ overflowX:'auto' }}>
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
        <thead><tr style={{ background:G.input }}>
          {['Customer','Partner','Amount','Status','Term','Monthly','Referral Fee'].map(h => <th key={h} style={styles.th}>{h}</th>)}
        </tr></thead>
        <tbody>
          {data.requests.slice(0, 20).map(r => (
            <tr key={r.id}>
              <td style={styles.td}>{r.contactName || '—'}</td>
              <td style={styles.td}>{r.partner}</td>
              <td style={styles.td}>{fmtUsd(r.approvedAmount || r.amount)}</td>
              <td style={styles.td}><Badge c={STATUS_COLOR[r.status]||G.textMuted} bg={(STATUS_COLOR[r.status]||G.textMuted)+'22'}>{r.status}</Badge></td>
              <td style={{ ...styles.td, color:G.textMuted }}>{r.termMonths ? `${r.termMonths}mo` : '—'}</td>
              <td style={{ ...styles.td, color:G.textMuted }}>{r.monthlyPayment ? fmtUsd(r.monthlyPayment) + '/mo' : '—'}</td>
              <td style={{ ...styles.td, color:G.purple, fontWeight:600 }}>{fmtUsd(r.referralFee)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {data.requests.length > 20 && <div style={{ fontSize:11, color:G.textMuted, marginTop:8, textAlign:'right' }}>Showing 20 of {data.requests.length}</div>}
  </div>;
}
